import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowUpRight, ShieldCheck, HeartHandshake,
  Microscope, Users, Compass, Sparkles, Star, Trophy, Building2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import BrandMark from '../components/BrandMark';

/**
 * WhyUs — public marketing page that explains what Freeze Dry Academy stands for.
 * Deliberately no fake stats: capability-based storytelling, four pillars, a
 * short "how we started" note, three team badges, and two CTAs (Pricing + Sign up).
 */
const pillars = [
  {
    icon: Microscope,
    title: 'Built by operators, not marketers',
    body: 'Every calculator, template, and lesson comes from real freeze-dry runs — home, prosumer, and industrial cabinets all included. If it doesn&apos;t hold up on our own shelves, it doesn&apos;t ship.',
  },
  {
    icon: ShieldCheck,
    title: 'Honest advice, no fluff',
    body: 'We&apos;ll tell you when NOT to buy a machine. We publish shelf-life and yield ranges based on cycle behaviour, not marketing brochures. Nothing we recommend has to convert you.',
  },
  {
    icon: HeartHandshake,
    title: 'A community that shares numbers',
    body: 'Threaded lessons, real batch logs, weekly Q&A. Members swap moisture readings, mylar sourcing, and equipment fixes with names attached — not anonymous review sites.',
  },
  {
    icon: Compass,
    title: 'You keep the wheel',
    body: 'Our tools output your data — export to PDF, CSV, or JSON any time. Cancel your membership and everything you built travels with you. No lock-in.',
  },
];

const badges = [
  { icon: Users, label: 'Community-owned roadmap' },
  { icon: Trophy, label: 'Built by working freeze-dry operators' },
  { icon: Star, label: 'Recommended in independent operator groups' },
];

const partners = [
  { name: 'Home freeze dryer operators', tag: 'Home + prosumer' },
  { name: 'Small-batch commercial cabinets', tag: 'Small-batch commercial' },
  { name: 'PackFreshUSA', tag: 'Barrier packaging' },
  { name: 'Retail Ready Consulting', tag: 'Business coaching' },
  { name: 'AAFD', tag: 'Industry association' },
];

const WhyUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="why-us-page">
      {/* Utility bar */}
      <div className="bg-navy text-background/80 text-xs">
        <div className="container mx-auto px-6 h-9 flex items-center justify-center gap-6">
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Built for operators — not marketing
          </span>
        </div>
      </div>

      {/* Main nav */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="lg" subtitle="Learning · Tools · Community" onClick={() => navigate('/')} />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-foreground/80">
            <button onClick={() => navigate('/platform')} className="hover:text-foreground transition">Platform</button>
            <button onClick={() => navigate('/directory')} className="hover:text-foreground transition">Directory</button>
            <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition">Pricing</button>
            <button className="text-foreground font-semibold">Why us</button>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="font-semibold" data-testid="why-nav-login">Log in</Button>
            <Button
              size="sm"
              onClick={() => navigate('/pricing')}
              className="rounded-full px-5 font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="why-nav-pricing-cta"
            >
              See pricing <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="bg-navy-spotlight text-background relative overflow-hidden">
        <div className="container mx-auto px-6 pt-16 pb-20 text-center relative z-10">
          <span className="pill-badge">
            <Sparkles className="w-3.5 h-3.5 fill-primary-foreground" /> Our story
          </span>
          <h1 className="font-heading mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-balance">
            The academy real freeze-dry
            <br />
            <span className="text-amber-brand">operators actually asked for.</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-background/75 leading-relaxed">
            Freeze Dry Academy was built by people who cycle machines every week — not marketers, not resellers.
            Everything you see here comes from a working freezer, a batch log, and a community that reads each other&apos;s numbers.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate('/pricing')}
              className="rounded-full px-7 py-6 font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="why-hero-pricing-btn"
            >
              See what&apos;s inside <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/login')}
              className="rounded-full px-7 py-6 font-bold bg-background/5 border-background/20 text-background hover:bg-background/10"
              data-testid="why-hero-signup-btn"
            >
              Start free
            </Button>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="container mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <div className="eyebrow text-primary">What we stand for</div>
          <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
            Four things we refuse to compromise on.
          </h2>
          <p className="mt-4 text-foreground/70 leading-relaxed">
            Every product decision — from calculator defaults to the AI&apos;s tone — is filtered through these.
            If a shortcut breaks one, we don&apos;t ship it.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {pillars.map(({ icon: Icon, title, body }, i) => (
            <div
              key={title}
              className="pro-card p-8"
              data-testid={`why-pillar-${i}`}
            >
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl font-extrabold text-foreground tracking-tight">{title}</h3>
              <p className="mt-3 text-foreground/70 leading-relaxed text-sm">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="bg-primary/5 border-y border-primary/20">
        <div className="container mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow text-primary">How we started</div>
            <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
              A shared spreadsheet, a broken machine, and a lot of ruined strawberries.
            </h2>
            <p className="mt-5 text-foreground/75 leading-relaxed">
              We built the first Business Calculator in 2023 to answer a single question:
              &ldquo;does this batch actually make money?&rdquo; The spreadsheet turned into a shared workbook, the workbook turned into
              a Discord, and the Discord turned into Freeze Dry Academy. Every feature since then started as a member
              asking the same practical question we had.
            </p>
            <p className="mt-5 text-foreground/75 leading-relaxed">
              We&apos;re still small. We answer support ourselves. If you email the founders you&apos;ll get a reply from
              a person who owns a freeze dryer.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {badges.map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                className="pro-card p-5 flex items-center gap-4"
                data-testid={`why-badge-${i}`}
              >
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <span className="font-heading font-extrabold text-foreground tracking-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS / CONNECTIONS */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="eyebrow text-primary">Who we&apos;re built with</div>
          <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
            Trusted by working operators and vetted partners.
          </h2>
          <p className="mt-4 text-foreground/70">
            These are the freeze-dry brands, cabinets, and consultancies we cite, cross-check with, and recommend inside the platform.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-4">
          {partners.map((p) => (
            <div
              key={p.name}
              className="pro-card p-5 text-center"
              data-testid={`why-partner-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="h-10 w-10 mx-auto rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                <Building2 className="w-5 h-5" />
              </div>
              <div className="font-heading text-sm font-extrabold text-foreground tracking-tight">{p.name}</div>
              <div className="text-xs text-foreground/60 mt-1">{p.tag}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Button
            variant="outline"
            onClick={() => navigate('/directory')}
            className="rounded-full px-6 font-bold"
            data-testid="why-directory-cta"
          >
            Browse the full directory <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" />
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-background">
        <div className="container mx-auto px-6 py-20 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">
            Come cycle with <span className="text-amber-brand">people who actually cycle.</span>
          </h2>
          <p className="mt-4 text-background/70 max-w-xl mx-auto">
            Free tier is genuinely free. Upgrade only when the paid tools start paying you back.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate('/pricing')}
              className="rounded-full px-7 py-6 font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="why-cta-pricing"
            >
              See pricing <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/login')}
              className="rounded-full px-7 py-6 font-bold bg-background/5 border-background/20 text-background hover:bg-background/10"
              data-testid="why-cta-signup"
            >
              Create free account
            </Button>
          </div>
        </div>
      </section>

      {/* Footer back-link */}
      <div className="container mx-auto px-6 py-6">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground transition"
          data-testid="why-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to landing
        </button>
      </div>
    </div>
  );
};

export default WhyUs;
